import React from 'react';
import { Info, ShieldAlert, Lock, Key, AlertTriangle, ClipboardCheck, CreditCard } from 'lucide-react';

/**
 * Компонент с информацией о безопасности паролей
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-violet-500" />
        Всё о паролях: защита, типы, статистика
      </h2>
      
      <div className="prose prose-violet dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-500" />
          Почему важна надежность паролей?
        </h3>
        <p>
          <strong>Пароли</strong> — это первая и часто единственная линия защиты ваших личных данных в интернете. По данным Лаборатории Касперского, в 2024 году в России ежедневно происходит более 100 000 попыток украсть пароли пользователей. Среди основных методов взлома:
        </p>
        <ul>
          <li><strong>Брутфорс (перебор)</strong> — автоматический подбор всех возможных комбинаций</li>
          <li><strong>Словарные атаки</strong> — использование словарей с популярными паролями</li>
          <li><strong>Социальная инженерия</strong> — выманивание паролей обманным путем</li>
          <li><strong>Утечки данных</strong> — кража баз данных сервисов с зашифрованными паролями</li>
        </ul>
        <p>
          Надежный пароль должен быть устойчив как минимум к первым двум типам атак.
        </p>

        <h3 className="flex items-center gap-2">
          <Key className="w-5 h-5 text-violet-500" />
          Характеристики надежного пароля
        </h3>
        <ul>
          <li><strong>Длина</strong> — минимум 12 символов. Каждый дополнительный символ существенно увеличивает стойкость к взлому.</li>
          <li><strong>Сложность</strong> — использование символов разных категорий (строчные, заглавные, цифры, специальные символы).</li>
          <li><strong>Непредсказуемость</strong> — отсутствие очевидных шаблонов и популярных слов.</li>
          <li><strong>Уникальность</strong> — разные пароли для разных сервисов.</li>
        </ul>
        
        <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-100 dark:border-violet-800 my-6">
          <h4 className="text-violet-700 dark:text-violet-300 font-medium">Энтропия пароля</h4>
          <p className="text-violet-600 dark:text-violet-400 text-sm mb-0">
            Энтропия — математическая мера случайности пароля, выраженная в битах. Пароль с энтропией 80 бит считается достаточно надежным для большинства целей, а 128 бит обеспечивает защиту от даже самых мощных вычислительных систем.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-green-500" />
          Типы паролей и их применение
        </h3>
        <p>
          В зависимости от ситуации могут использоваться разные типы паролей:
        </p>
        <ul>
          <li>
            <strong>Случайный пароль</strong> — самый надежный вариант, представляет собой случайную последовательность символов. Подходит для критически важных аккаунтов (банки, электронная почта).
          </li>
          <li>
            <strong>Запоминаемый пароль</strong> — состоит из случайных слов, разделенных знаками. Проще для запоминания, но сохраняет высокую надежность.
          </li>
          <li>
            <strong>PIN-код</strong> — короткая последовательность цифр. Используется для банковских карт и мобильных устройств.
          </li>
          <li>
            <strong>Парольная фраза</strong> — длинное предложение или набор слов. Высокая надежность за счет длины.
          </li>
        </ul>
        
        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Распространенные ошибки
        </h3>
        <p>
          По статистике НКЦКИ (Национального координационного центра по компьютерным инцидентам), россияне часто допускают следующие ошибки при создании паролей:
        </p>
        <ul>
          <li>Использование личной информации (даты рождения, имена, номера телефонов)</li>
          <li>Замена букв на похожие цифры в очевидной манере (o → 0, i → 1)</li>
          <li>Простые модификации популярных паролей (qwerty → qwerty123)</li>
          <li>Использование одного пароля на множестве сайтов</li>
          <li>Редкая смена паролей (или отсутствие таковой)</li>
        </ul>
        
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 my-6">
          <h4 className="text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Самые небезопасные пароли в России (2024)
          </h4>
          <ol className="text-amber-600 dark:text-amber-400 text-sm mb-0 columns-2">
            <li>qwerty</li>
            <li>12345</li>
            <li>password</li>
            <li>admin</li>
            <li>123456789</li>
            <li>russia</li>
            <li>пароль</li>
            <li>любовь</li>
            <li>qwertyuiop</li>
            <li>987654321</li>
          </ol>
        </div>

        <h3 className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-blue-500" />
          Практические рекомендации
        </h3>
        <ul>
          <li>Используйте <strong>менеджер паролей</strong> для хранения сложных уникальных паролей</li>
          <li>Включите <strong>двухфакторную аутентификацию</strong> на важных аккаунтах</li>
          <li>Регулярно <strong>проверяйте</strong>, не появились ли ваши учетные данные в утечках</li>
          <li>Меняйте пароли важных аккаунтов не реже одного раза в 6 месяцев</li>
          <li>Не сохраняйте пароли в текстовых файлах на компьютере</li>
          <li>Не пересылайте пароли по электронной почте или в мессенджерах</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-indigo-500" />
          Защита финансовых аккаунтов
        </h3>
        <p>
          Для аккаунтов с доступом к финансам (онлайн-банкинг, платежные системы, криптовалютные кошельки) рекомендуется:
        </p>
        <ol>
          <li><strong>Максимально сложные пароли</strong> — длиной от 16 символов со всеми типами символов</li>
          <li><strong>Аппаратные ключи безопасности</strong> или <strong>биометрия</strong> в качестве второго фактора</li>
          <li><strong>Отдельный email</strong> для финансовых сервисов</li>
          <li><strong>Регулярная проверка</strong> активности аккаунта на подозрительные действия</li>
        </ol>

        <h3>Законодательство России в сфере защиты паролей</h3>
        <p>
          В России действуют нормативные акты, регулирующие требования к паролям в информационных системах:
        </p>
        <ul>
          <li>ФЗ-152 «О персональных данных» и подзаконные акты ФСТЭК устанавливают требования к защите учетных данных</li>
          <li>Организации финансового сектора должны соблюдать требования ЦБ РФ по информационной безопасности (ГОСТ Р 57580)</li>
          <li>Для ГИС и объектов КИИ действуют специальные требования по защите доступа</li>
        </ul>
        
        <p className="font-semibold mt-6">
          Использование наших инструментов генерации паролей поможет вам создавать надежные и уникальные пароли, соответствующие всем современным требованиям безопасности.
        </p>
        
        <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 my-4">
          <h4 className="text-gray-900 dark:text-white font-medium mb-2">Ссылки на полезные ресурсы</h4>
          <ul className="text-sm space-y-1">
            <li><a href="https://www.gosuslugi.ru/help/faq/login/1" className="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300">Госуслуги: Рекомендации по безопасности</a></li>
            <li><a href="https://www.cbr.ru/information_security/acts/" className="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300">ЦБ РФ: Нормативные акты по информационной безопасности</a></li>
            <li><a href="https://www.fstec.ru/tekhnicheskaya-zashchita-informatsii/" className="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300">ФСТЭК России: Техническая защита информации</a></li>
            <li><a href="https://www.nccci.ru/tips/" className="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300">НКЦКИ: Рекомендации по защите</a></li>
          </ul>
        </div>
      </div>
    </article>
  );
};

export default InfoContent;